/**
 * Hala Control Center - Mapping Engine
 * Premium needs-to-services mapping management
 */

import { useState, useEffect } from 'react';
import { 
  GitBranch, 
  Plus, 
  Edit2, 
  Trash2, 
  Power,
  ChevronRight,
  ArrowLeft,
  Target,
  Layers,
  Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/control-center`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#1D8F4E',
  warning: '#F59E0B',
  error: '#EF4444',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const NEEDS = [
  { value: 'sell_products', label: 'بيع منتجات', icon: '🛒' },
  { value: 'develop_business', label: 'تطوير المشروع', icon: '📈' },
  { value: 'learn_skills', label: 'تعلم مهارات', icon: '📚' },
  { value: 'get_consultation', label: 'الحصول على استشارة', icon: '💬' },
  { value: 'find_workspace', label: 'إيجاد مساحة عمل', icon: '🏢' },
  { value: 'start_business', label: 'بدء مشروع', icon: '🚀' },
  { value: 'expand_business', label: 'توسيع المشروع', icon: '🌟' },
  { value: 'get_funding', label: 'الحصول على تمويل', icon: '💰' }
];

export default function MappingPage() {
  const [rules, setRules] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNeed, setSelectedNeed] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, programsRes, modulesRes] = await Promise.all([
        fetch(`${API}/mapping-rules`),
        fetch(`${API}/programs`),
        fetch(`${API}/modules`)
      ]);
      
      setRules(await rulesRes.json());
      setPrograms(await programsRes.json());
      setModules(await modulesRes.json());
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  };

  const getRulesForNeed = (needValue) => {
    return rules.filter(r => r.need_type === needValue);
  };

  const getProgramName = (programId) => {
    return programs.find(p => p.program_id === programId)?.name_ar || programId;
  };

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: COLORS.text, margin: '0 0 8px' }}>محرك التوصيات</h1>
        <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: 0 }}>إدارة قواعد ربط الاحتياجات بالخدمات</p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
          جاري التحميل...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px' }}>
          {/* Needs List */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden'
          }}>
            <div style={{ padding: '20px', borderBottom: `1px solid ${COLORS.border}` }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>الاحتياجات</h2>
            </div>
            <div>
              {NEEDS.map(need => {
                const needRules = getRulesForNeed(need.value);
                const isSelected = selectedNeed === need.value;
                
                return (
                  <div
                    key={need.value}
                    onClick={() => setSelectedNeed(need.value)}
                    style={{
                      padding: '16px 20px',
                      borderBottom: `1px solid ${COLORS.border}`,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? `${COLORS.primary}10` : 'transparent',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '24px' }}>{need.icon}</span>
                        <div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>{need.label}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                            {needRules.length} قاعدة
                          </p>
                        </div>
                      </div>
                      <ChevronRight size={18} color={COLORS.textMuted} style={{ transform: isSelected ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Rules Panel */}
          <div>
            {selectedNeed ? (
              <div style={{
                backgroundColor: COLORS.white,
                borderRadius: '16px',
                border: `1px solid ${COLORS.border}`,
                overflow: 'hidden'
              }}>
                <div style={{ 
                  padding: '20px', 
                  borderBottom: `1px solid ${COLORS.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '28px' }}>{NEEDS.find(n => n.value === selectedNeed)?.icon}</span>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
                        {NEEDS.find(n => n.value === selectedNeed)?.label}
                      </h2>
                      <p style={{ margin: '2px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                        قواعد التوصية
                      </p>
                    </div>
                  </div>
                  <button style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: COLORS.primary,
                    color: COLORS.white,
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}>
                    <Plus size={16} />
                    إضافة قاعدة
                  </button>
                </div>

                {getRulesForNeed(selectedNeed).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px', color: COLORS.textMuted }}>
                    <GitBranch size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                    <p style={{ margin: 0 }}>لا توجد قواعد توصية لهذا الاحتياج</p>
                    <p style={{ margin: '8px 0 0', fontSize: '13px' }}>أضف قاعدة جديدة لربط الخدمات</p>
                  </div>
                ) : (
                  <div style={{ padding: '20px' }}>
                    {getRulesForNeed(selectedNeed).map((rule, i) => (
                      <div key={rule.rule_id} style={{
                        padding: '16px',
                        backgroundColor: COLORS.bg,
                        borderRadius: '12px',
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <div>
                            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>{rule.name_ar}</h3>
                            <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textMuted }}>الأولوية: {rule.priority}</p>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              backgroundColor: rule.status === 'active' ? `${COLORS.success}15` : `${COLORS.textLight}15`,
                              color: rule.status === 'active' ? COLORS.success : COLORS.textLight,
                              fontSize: '11px'
                            }}>
                              {rule.status === 'active' ? 'نشط' : 'معطل'}
                            </span>
                            <button style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '6px',
                              border: 'none',
                              backgroundColor: COLORS.white,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Edit2 size={14} color={COLORS.textMuted} />
                            </button>
                          </div>
                        </div>
                        
                        {/* Target Programs */}
                        {rule.target_programs && rule.target_programs.length > 0 && (
                          <div style={{ marginBottom: '8px' }}>
                            <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 6px' }}>البرامج المستهدفة:</p>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {rule.target_programs.map(pid => (
                                <span key={pid} style={{
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  backgroundColor: COLORS.white,
                                  fontSize: '12px',
                                  color: COLORS.text
                                }}>
                                  {getProgramName(pid)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Conditions */}
                        {rule.city_filter && rule.city_filter.length > 0 && (
                          <div>
                            <p style={{ fontSize: '12px', color: COLORS.textMuted, margin: '0 0 6px' }}>المدن:</p>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {rule.city_filter.map(city => (
                                <span key={city} style={{
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  backgroundColor: `${COLORS.gold}20`,
                                  fontSize: '11px',
                                  color: COLORS.gold
                                }}>
                                  {city}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                backgroundColor: COLORS.white,
                borderRadius: '16px',
                border: `1px solid ${COLORS.border}`,
                padding: '80px 40px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: `${COLORS.primary}10`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <GitBranch size={36} color={COLORS.primary} />
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>محرك التوصيات</h3>
                <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                  اختر احتياج من القائمة لعرض وإدارة قواعد التوصية المرتبطة به
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
